/**
 * Draggable text components for the banner editor canvas
 */
import { useDraggable } from "@dnd-kit/core";

/**
 * Get transform style for text alignment
 */
const getAlignmentTransform = (align) => {
  switch (align) {
    case 'center':
      return 'translateX(-50%)';
    case 'right':
      return 'translateX(-100%)';
    default:
      return ''; // Return empty string for left alignment
  }
};

/**
 * Draggable Custom Text component
 */
export function DraggableText({ banner }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'draggable-text',
  });

  const shadowStyle = banner.text_shadow 
    ? `${banner.text_shadow_x}px ${banner.text_shadow_y}px ${banner.text_shadow_blur}px ${banner.text_shadow_color}`
    : 'none';

  const alignTransform = getAlignmentTransform(banner.text_align || 'left');
  
  // Build transform: drag transform first, then alignment
  let transformValue;
  if (transform) {
    transformValue = alignTransform 
      ? `translate3d(${transform.x}px, ${transform.y}px, 0) ${alignTransform}`
      : `translate3d(${transform.x}px, ${transform.y}px, 0)`;
  } else {
    transformValue = alignTransform || undefined;
  }

  const style = {
    transform: transformValue,
    transformOrigin: 'top left',
    left: `${banner.text_position_x}%`,
    top: `${banner.text_position_y}%`,
    fontFamily: `'${banner.text_font}', sans-serif`,
    color: banner.text_color,
    fontSize: `${banner.text_size}px`,
    fontWeight: banner.text_font_weight || 700,
    textShadow: shadowStyle,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="absolute cursor-move"
      data-testid="draggable-text"
    >
      {banner.custom_text}
    </div>
  );
}

/**
 * Draggable Result Text component (from lottery)
 */
export function DraggableResultText({ banner, resultText }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'draggable-result-text',
  });

  const shadowStyle = banner.result_text_shadow 
    ? `${banner.result_text_shadow_x}px ${banner.result_text_shadow_y}px ${banner.result_text_shadow_blur}px ${banner.result_text_shadow_color}`
    : 'none';

  const alignTransform = getAlignmentTransform(banner.result_text_align || 'left');
  
  // Build transform: drag transform first, then alignment
  let transformValue;
  if (transform) {
    transformValue = alignTransform 
      ? `translate3d(${transform.x}px, ${transform.y}px, 0) ${alignTransform}`
      : `translate3d(${transform.x}px, ${transform.y}px, 0)`;
  } else {
    transformValue = alignTransform || undefined;
  }

  const style = {
    transform: transformValue,
    transformOrigin: 'top left',
    left: `${banner.result_text_position_x}%`,
    top: `${banner.result_text_position_y}%`,
    fontFamily: `'${banner.result_text_font}', sans-serif`,
    color: banner.result_text_color,
    fontSize: `${banner.result_text_size}px`,
    fontWeight: banner.result_text_font_weight || 700,
    textShadow: shadowStyle,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="absolute cursor-move"
      data-testid="draggable-result-text"
    >
      {resultText}
    </div>
  );
}

/**
 * Draggable Numbers container component
 */
export function DraggableNumbers({ banner, children }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'draggable-numbers',
  });

  const style = {
    transform: transform 
      ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${banner.scale})` 
      : `scale(${banner.scale})`,
    transformOrigin: 'top left',
    left: `${banner.position_x}%`,
    top: `${banner.position_y}%`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="absolute cursor-move flex items-center"
      data-testid="draggable-numbers"
    >
      <div className="flex items-center" style={{ gap: `${banner.number_gap}px` }}>
        {children}
      </div>
    </div>
  );
}
